import axios from "axios";
import { Drawer } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineMenu } from "react-icons/hi";
import I404Png from "./../images/404Basil.png";
import BottomSheet from "./qrmenu/BottomSheet";
import CategoryRail from "./qrmenu/CategoryRail";
import CategorySections from "./qrmenu/CategorySections";
import HoursDetail from "./qrmenu/HoursDetail";
import ItemDetail from "./qrmenu/ItemDetail";
import MenuHeader from "./qrmenu/MenuHeader";
import { paletteFor } from "./qrmenu/palette";
import SidePanelContent from "./SidePanelContent";
import "./qrmenu/menu.css";

interface StoreHours {
  menuDay: string;
  fromTime: string;
  toTime: string;
}
interface MerchantStore {
  logoUrl?: string;
  title?: string;
  subTitle?: string;
  customTextOne?: string;
  storeHours?: StoreHours[];
}
interface Category {
  id: number;
  name: string;
  items?: Product[];
}
interface Menu {
  id: number;
  menuName: string;
  categories?: Category[];
}
interface ModifierItem {
  id: number;
  orderBy?: number;
  modifierItem: { name: string; price: number };
}
interface ModifierSet {
  id?: number;
  name: string;
  noOfRequired?: number;
  maximum?: number;
  orderBy?: number | null;
  modifierSetModifierItems?: ModifierItem[];
}
interface Product {
  id: number;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
  modifierSets?: ModifierSet[];
}
interface ResponseData {
  merchantStore: MerchantStore;
  all: Category[] | null;
  menus: Menu[];
}

type SheetState =
  | { kind: "item"; product: Product; categoryName: string }
  | { kind: "hours" }
  | null;

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const isOpenNow = (fromTime: string, toTime: string): boolean => {
  if (!fromTime || !toTime) return false;
  const now = new Date();
  const [fh, fm] = fromTime.split(":").map(Number);
  const [th, tm] = toTime.split(":").map(Number);
  const from = new Date(now);
  from.setHours(fh, fm, 0, 0);
  const to = new Date(now);
  to.setHours(th, tm, 0, 0);
  if (to <= from) to.setDate(to.getDate() + 1);
  return now >= from && now <= to;
};

const computeOpen = (schedule: StoreHours[] | undefined): boolean => {
  if (!schedule || schedule.length === 0) return false;
  const today = DAYS[new Date().getDay()];
  return schedule
    .filter((s) => s.menuDay === today)
    .some((s) => isOpenNow(s.fromTime, s.toTime));
};

const sortItemsAlphabetically = <T extends { name?: string }>(items: T[]): T[] =>
  items.slice().sort((a, b) => {
    const nameA = (a.name || "").trim().toLowerCase();
    const nameB = (b.name || "").trim().toLowerCase();
    const isNumA = /^[0-9]/.test(nameA.charAt(0));
    const isNumB = /^[0-9]/.test(nameB.charAt(0));
    if (isNumA && !isNumB) return -1;
    if (!isNumA && isNumB) return 1;
    return nameA.localeCompare(nameB);
  });

const withSortedItems = (categories: Category[]): Category[] =>
  categories
    .filter((c) => c?.items && c.items.length > 0)
    .map((c) => ({ ...c, items: sortItemsAlphabetically(c.items as Product[]) }));

const monogramFor = (s: string | undefined): string => {
  const t = (s || "").trim();
  if (!t) return "•";
  const parts = t.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("");
};

const QRMenu = () => {
  const merchantName = useMemo(
    () => window.location.pathname.split("/")[1] || "",
    [],
  );

  const [categories, setCategories] = useState<Category[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
  const [merchantStore, setMerchantStore] = useState<MerchantStore | null>(null);
  const [storeHours, setStoreHours] = useState<StoreHours[]>([]);
  const [hasError, setHasError] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const [openNow, setOpenNow] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<number, HTMLElement | null>>({});

  // Fetch
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://basil-backoffice-dev.com/api/menu/qr",
          { headers: { "Content-Type": "application/json", MID: merchantName } },
        );
        if (cancelled) return;
        const data = response.data as ResponseData;

        const menusData = data?.menus || [];
        setMenus(menusData);
        if (menusData.length > 0) setSelectedMenu(menusData[0]);

        const raw =
          data?.all !== null ? (data.all as Category[]) : menusData[0]?.categories || [];
        if (!raw || raw.length === 0) {
          setHasError(true);
          return;
        }
        const sorted = withSortedItems(raw);
        if (sorted.length === 0) {
          setHasError(true);
          return;
        }
        setCategories(sorted);
        setActiveCategoryId(sorted[0].id);

        const hours = (data?.merchantStore?.storeHours as StoreHours[]) || [];
        setStoreHours(hours);
        setMerchantStore({
          ...data?.merchantStore,
          customTextOne: data?.merchantStore?.subTitle ?? "",
        });
        if (data?.merchantStore?.logoUrl) {
          localStorage.setItem("onlineOrderSettingsLogo", data.merchantStore.logoUrl);
        }
      } catch (err) {
        if (!cancelled) setHasError(true);
        console.error("Error fetching menu data:", err);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [merchantName]);

  // Open/closed status — recomputed each minute
  useEffect(() => {
    const compute = () => setOpenNow(computeOpen(storeHours));
    compute();
    const id = setInterval(compute, 60_000);
    return () => clearInterval(id);
  }, [storeHours]);

  // IntersectionObserver — highlight active chip on scroll
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || categories.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idAttr = e.target.getAttribute("data-cat");
            if (idAttr) setActiveCategoryId(Number(idAttr));
          }
        });
      },
      { root, rootMargin: "-25% 0px -65% 0px", threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [categories]);

  const selectCategory = (id: number) => {
    setActiveCategoryId(id);
    const el = sectionRefs.current[id];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
    }
  };

  const switchMenu = (menu: Menu) => {
    setSelectedMenu(menu);
    const sorted = withSortedItems(menu.categories || []);
    setCategories(sorted);
    setActiveCategoryId(sorted[0]?.id ?? null);
    setMenuDrawerOpen(false);
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 });
  };

  const title = merchantStore?.title || merchantName || "Menu";
  const tagline = merchantStore?.customTextOne || merchantStore?.subTitle;
  const monogram = monogramFor(merchantStore?.title || merchantName);
  const visibleMenus = menus.filter((m) => (m.categories?.length || 0) > 0);

  if (hasError) {
    return (
      <div className="menu-404">
        <div className="menu-404__inner">
          <img src={I404Png} alt="Not found" />
          <h2>Business not found</h2>
          <p>The business you are looking for was not found. Please check the link and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-app">
      <div className="menu-scroll" ref={scrollRef}>
        <div className="menu-container">
          <MenuHeader
            title={title}
            tagline={tagline}
            logoUrl={merchantStore?.logoUrl}
            monogram={monogram}
            openNow={openNow}
            onOpenHours={() => setSheet({ kind: "hours" })}
            onOpenSettings={() => setSettingsOpen(true)}
          />

          {visibleMenus.length > 1 && (
            <div style={{ padding: "0 22px 6px" }}>
              <button
                className="menu-header__hours"
                onClick={() => setMenuDrawerOpen(true)}
                style={{ paddingLeft: 12 }}
              >
                <HiOutlineMenu />
                <span className="menu-header__hours-text">
                  {selectedMenu?.menuName || "Menus"} <em>change</em>
                </span>
              </button>
            </div>
          )}
        </div>

        {categories.length > 0 && (
          <CategoryRail
            categories={categories.map((c) => ({
              id: c.id,
              name: c.name,
              itemCount: c.items?.length || 0,
            }))}
            activeId={activeCategoryId}
            onSelect={selectCategory}
          />
        )}

        {categories.length === 0 ? (
          <p className="menu-empty menu-container">Loading menu…</p>
        ) : (
          <CategorySections
            categories={categories}
            sectionRefs={sectionRefs}
            onOpenItem={(product, categoryName) =>
              setSheet({ kind: "item", product, categoryName })
            }
          />
        )}

        <footer className="menu-foot">
          <span>{title}</span>
          <span className="menu-foot__rule" />
          <span>QR menu</span>
        </footer>
      </div>

      <BottomSheet open={!!sheet} onClose={() => setSheet(null)}>
        {sheet?.kind === "item" && (
          <ItemDetail
            product={sheet.product}
            categoryName={sheet.categoryName}
            onClose={() => setSheet(null)}
          />
        )}
        {sheet?.kind === "hours" && (
          <HoursDetail schedule={storeHours} onClose={() => setSheet(null)} />
        )}
      </BottomSheet>

      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "320px" } }}
      >
        <SidePanelContent setIsOpen={setSettingsOpen} />
      </Drawer>

      <Drawer
        anchor="left"
        open={menuDrawerOpen}
        onClose={() => setMenuDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: "280px" } }}
      >
        <div style={{ padding: 24 }}>
          <h3
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontSize: 22,
              fontWeight: 500,
              margin: "0 0 16px",
              letterSpacing: "-0.01em",
            }}
          >
            Menus
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {visibleMenus.map((m) => {
              const active = selectedMenu?.id === m.id;
              const pal = paletteFor(m.menuName);
              return (
                <button
                  key={m.id}
                  onClick={() => switchMenu(m)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid",
                    borderColor: active ? "currentColor" : "rgba(0,0,0,0.08)",
                    background: active ? pal.surface : "transparent",
                    color: active ? pal.ink : "inherit",
                    fontFamily: "Manrope, sans-serif",
                    fontSize: 14,
                    fontWeight: active ? 600 : 500,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: pal.primary,
                    }}
                  />
                  {m.menuName}
                </button>
              );
            })}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default QRMenu;
